import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { Avatar } from 'primereact/avatar';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import AddBranchModal from './AddBranchModal';
import EditBranchModal from './EditBranchModal';

interface Branch {
  branch_id: string;
  branch_code?: string;
  institute_id: string;
  name: string;
  license_number: string;
  license_start: string;
  license_expiry: string;
  state?: string;
  district?: string;
  village?: string;
  branch_type?: string;
  address: string;
  pincode: number;
  total_pc: number;
  latitude: number;
  longitude: number;
  opening_time: string;
  closing_time: string;
  capacity: number;
  status: string;
  active_operator: {
    user_id: string;
    name: string;
    email: string;
    assigned_at: string;
  } | null;
  created_at: string;
}

interface StudentCount {
  branch_id: string;
  institute_id: string;
  total: number;
  active: number;
  inactive: number;
  enrolled: number;
}

interface ApiResponse {
  success: boolean;
  data: Branch[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface StudentCountApiResponse {
  success: boolean;
  data: StudentCount;
}

interface BranchesProps {
  onBranchClick?: (branchId: string) => void;
}

const BRANCH_TYPE_LABELS: Record<string, string> = {
  asc: 'ASC',
  franchise: 'Franchise',
  main: 'Main Branch',
};

const getBranchTypeLabel = (type?: string) =>
  type ? (BRANCH_TYPE_LABELS[type] ?? type) : null;

// ── Skeleton shimmer for student count ────────────────────────────────────────
const CountSkeleton: React.FC = () => (
  <div style={{ display: 'flex', gap: '8px' }}>
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        style={{
          flex: 1,
          height: '52px',
          borderRadius: '8px',
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.4s infinite',
        }}
      />
    ))}
    <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
  </div>
);

// ── Student count pill row ─────────────────────────────────────────────────────
const StudentCountRow: React.FC<{ count: StudentCount | null; loading: boolean }> = ({
  count,
  loading,
}) => {
  if (loading) return <CountSkeleton />;
  if (!count) return null;

  const pills = [
    { label: 'Total',    value: count.total,    bg: '#f0f7ff', color: '#1565c0', border: '#bbdefb' },
    { label: 'Active',   value: count.active,   bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
    { label: 'Enrolled', value: count.enrolled, bg: '#fdf4ff', color: '#6b21a8', border: '#e9d5ff' },
  ];

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      {pills.map(({ label, value, bg, color, border }) => (
        <div
          key={label}
          style={{
            flex: 1,
            padding: '8px 6px',
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <p style={{ margin: '0 0 2px 0', fontSize: '1rem', fontWeight: '700', color }}>{value}</p>
          <p
            style={{
              margin: 0,
              fontSize: '0.65rem',
              fontWeight: '600',
              color,
              opacity: 0.75,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
            }}
          >
            {label}
          </p>
        </div>
      ))}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const Branches: React.FC<BranchesProps> = ({ onBranchClick }) => {
  const toast    = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});

  const [branches, setBranches]             = useState<Branch[]>([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [search, setSearch]                 = useState('');
  const [page, setPage]                     = useState(1);
  const [pagination, setPagination]         = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [showAddModal, setShowAddModal]     = useState(false);
  const [showEditModal, setShowEditModal]   = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [deleteDialog, setDeleteDialog]     = useState<{
    visible: boolean;
    branchId: string;
    branchName: string;
  }>({ visible: false, branchId: '', branchName: '' });

  // Student counts keyed by branch_id
  const [studentCounts, setStudentCounts]   = useState<Record<string, StudentCount>>({});
  const [countsLoading, setCountsLoading]   = useState<Record<string, boolean>>({});

  const { data: dashboardData, loading: dashboardLoading, fetchDashboardData } =
    useSuperAdminDashboardStore();

  useEffect(() => {
    if (!dashboardData) fetchDashboardData();
  }, [dashboardData, fetchDashboardData]);

  // Inject PrimeReact style overrides once
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .branch-card .p-card-body { padding: 0 !important; }
      .branch-card .p-card-content { padding: 0 !important; }
      .p-menu { border-radius: 12px !important; border: 1px solid #e0e0e0 !important; box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important; padding: 8px !important; }
      .p-menu .p-menuitem-link { border-radius: 8px !important; padding: 10px 12px !important; margin: 2px 0 !important; transition: all 0.2s ease !important; }
      .p-menu .p-menuitem-link:hover { background: #f5f5f5 !important; }
      .p-menu .p-menuitem-icon { color: #0f3d3e !important; font-size: 0.9rem !important; }
      .p-menu .p-menuitem-text { color: #1a1a1a !important; font-weight: 600 !important; font-size: 0.85rem !important; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => { fetchBranches(); }, [page, search]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search }),
      });
      const response = await apiClient.get<ApiResponse>(`/v1/branches?${params}`);
      if (response.data.success) {
        setBranches(response.data.data);
        setPagination(response.data.pagination);
        fetchAllStudentCounts(response.data.data);
      } else {
        setError('Failed to load branches');
      }
    } catch (err: any) {
      console.error('Failed to fetch branches:', err);
      setError(err.response?.data?.error || 'Failed to load branches');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load branches',
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch student counts for all branches in parallel.
   * Endpoint: GET /v1/students/branch-student-count?branch_id=<id>
   * Response: { success: boolean; data: StudentCount }
   */
  const fetchAllStudentCounts = async (branchList: Branch[]) => {
    // Mark all branches as loading
    const initialLoading: Record<string, boolean> = {};
    branchList.forEach((b) => { initialLoading[b.branch_id] = true; });
    setCountsLoading(initialLoading);

    const results = await Promise.allSettled(
      branchList.map((b) =>
        apiClient
          .get<StudentCountApiResponse>(
            `/v1/students/branch-student-count?branch_id=${b.branch_id}`
          )
          .then((res) => {
            if (!res.data.success) throw new Error('API returned success: false');
            return res.data.data;
          })
      )
    );

    const newCounts: Record<string, StudentCount>  = {};
    const newLoading: Record<string, boolean>      = {};

    branchList.forEach((b, i) => {
      const result = results[i];
      if (result.status === 'fulfilled') {
        newCounts[b.branch_id] = result.value;
      }
      // fulfilled OR rejected — either way we're done loading
      newLoading[b.branch_id] = false;
    });

    setStudentCounts((prev) => ({ ...prev, ...newCounts }));
    setCountsLoading((prev) => ({ ...prev, ...newLoading }));
  };

  const handleAddBranchSuccess  = () => { fetchBranches(); };
  const handleEditBranchSuccess = () => { fetchBranches(); };
  const handleEditClick         = (branch: Branch) => {
    setSelectedBranch(branch);
    setShowEditModal(true);
  };

  const handleDeleteBranch = (branchId: string, branchName: string) => {
    setDeleteDialog({ visible: true, branchId, branchName });
  };

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/branches/${deleteDialog.branchId}`);
      toast.current?.show({
        severity: 'success',
        summary: 'Deleted',
        detail: 'Branch deleted successfully',
        life: 3000,
      });
      fetchBranches();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.error || 'Failed to delete branch',
        life: 4000,
      });
    } finally {
      setDeleteDialog({ visible: false, branchId: '', branchName: '' });
    }
  };

  const getMenuItems = (branch: Branch) => [
    {
      label: 'View Details',
      icon: 'pi pi-eye',
      command: () => onBranchClick && onBranchClick(branch.branch_id),
    },
    { label: 'Edit',   icon: 'pi pi-pencil', command: () => handleEditClick(branch) },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => handleDeleteBranch(branch.branch_id, branch.name),
    },
  ];

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit', month: '2-digit', year: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // ── Guards ──────────────────────────────────────────────────────────────────

  if (dashboardLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
        <p style={{ color: '#0f3d3e', fontSize: '0.9rem' }}>Loading dashboard data...</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ padding: '24px' }}>
        <Message
          severity="warn"
          text="Unable to load dashboard data. Please refresh the page."
          style={{ fontSize: '0.9rem' }}
        />
      </div>
    );
  }

  if (loading && branches.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: '#f5f5f5', borderRadius: '12px' }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
          <p style={{ marginTop: '16px', color: '#0f3d3e', fontSize: '0.95rem' }}>Loading branches...</p>
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />
      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, branchId: '', branchName: '' })}
        onConfirm={confirmDelete}
        title="Delete Branch"
        message={`Are you sure you want to delete "${deleteDialog.branchName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        borderRadius: '20px', padding: '32px', marginBottom: '24px', color: 'white',
        position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(15,61,62,0.3)',
      }}>
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(212,241,126,0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212,241,126,0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Branch Management
              </div>
              <h1 style={{ margin: '0 0 8px 0', fontSize: '1.7rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>Branches</h1>
              <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                Manage all branches across institutes
              </p>
              <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                <span>
                  <i className="pi pi-building" style={{ marginRight: '6px' }}></i>
                  {pagination.total} Total Branches
                </span>
              </div>
            </div>
            <GlobalButton
              label="Add Branch"
              variant="success"
              icon="pi pi-plus"
              onClick={() => setShowAddModal(true)}
            />
          </div>
        </div>
      </div>

      {/* ── Search ── */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
        <div style={{ position: 'relative' }}>
          <i className="pi pi-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#999', fontSize: '0.9rem' }}></i>
          <InputText
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, branch ID, state, district..."
            style={{ width: '100%', paddingLeft: '42px', height: '44px', border: '2px solid #f0f0f0', borderRadius: '12px', fontSize: '0.9rem' }}
          />
        </div>
      </div>

      {error && (
        <Message
          severity="error"
          text={error}
          style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
        />
      )}

      {/* ── Grid ── */}
      {branches.length === 0 && !loading ? (
        <div style={{ padding: '80px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #e0e0e0' }}>
          <i className="pi pi-building" style={{ fontSize: '3rem', color: '#ddd', marginBottom: '16px' }}></i>
          <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>
            No branches found
          </h3>
          <p style={{ margin: '0 0 20px 0', color: '#999', fontSize: '0.9rem' }}>
            {search ? 'Try adjusting your search criteria' : 'Get started by adding your first branch'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(288px, 1fr))', gap: '16px' }}>
          {branches.map((branch) => (
            <Card
              key={branch.branch_id}
              className="branch-card"
              style={{
                borderRadius: '20px', border: 'none', background: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden',
                transition: 'all 0.3s ease', cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(15,61,62,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
              }}
            >
              {/* ── Card Header ── */}
              <div style={{
                background: 'linear-gradient(180deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
                padding: '20px', color: 'white', borderRadius: '20px 20px 0 0',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: '600', color: 'white' }}>
                      {branch.name}
                    </h3>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      {branch.branch_code && (
                        <span style={{ fontSize: '0.7rem', fontFamily: 'monospace', color: '#d4f17e', fontWeight: '700' }}>
                          {branch.branch_code}
                        </span>
                      )}
                      {branch.branch_type && (
                        <span style={{ padding: '2px 8px', background: 'rgba(212,241,126,0.2)', borderRadius: '10px', fontSize: '0.65rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                          {getBranchTypeLabel(branch.branch_type)}
                        </span>
                      )}
                    </div>
                    {(branch.district || branch.state) && (
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                        <i className="pi pi-map-marker" style={{ marginRight: '4px', fontSize: '0.65rem' }}></i>
                        {[branch.district, branch.state].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                  <span style={{
                    padding: '5px 12px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '600',
                    background: branch.status === 'active' ? '#d4f17e' : '#ffcdd2',
                    color: branch.status === 'active' ? '#0f3d3e' : '#b71c1c',
                    textTransform: 'capitalize', flexShrink: 0,
                  }}>
                    {branch.status}
                  </span>
                </div>

                {/* Capacity + PC row */}
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div>
                    <p style={{ margin: '0 0 2px 0', fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Capacity</p>
                    <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: 'white' }}>{branch.capacity}</p>
                  </div>
                  {branch.total_pc > 0 && (
                    <div>
                      <p style={{ margin: '0 0 2px 0', fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PCs</p>
                      <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: 'white' }}>{branch.total_pc}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Card Body ── */}
              <div style={{ padding: '16px 20px 20px' }}>

                {/* Student counts */}
                <div style={{ marginBottom: '16px' }}>
                  <p style={{
                    margin: '0 0 8px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600',
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                    <i className="pi pi-users" style={{ fontSize: '0.7rem' }}></i>
                    Students
                  </p>
                  <StudentCountRow
                    count={studentCounts[branch.branch_id] ?? null}
                    loading={countsLoading[branch.branch_id] ?? false}
                  />
                </div>

                {/* License Info */}
                <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px dashed #e0e0e0' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>License</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', background: '#0f3d3e', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="pi pi-id-card" style={{ color: '#d4f17e', fontSize: '0.9rem' }}></i>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 2px 0', fontSize: '0.8rem', fontWeight: '600', color: '#1a1a1a', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {branch.license_number}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.7rem', color: '#666' }}>
                        Expires {formatDate(branch.license_expiry)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Active Operator */}
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Operator</p>
                  {branch.active_operator ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Avatar
                        label={branch.active_operator.name.charAt(0)}
                        size="normal"
                        style={{ backgroundColor: '#0f3d3e', color: '#d4f17e', width: '36px', height: '36px', fontSize: '0.9rem', fontWeight: '600' }}
                        shape="circle"
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: '0 0 2px 0', fontSize: '0.85rem', fontWeight: '600', color: '#1a1a1a' }}>
                          {branch.active_operator.name}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {branch.active_operator.email}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '10px', background: '#f5f5f5', borderRadius: '8px', textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '0.8rem', color: '#999', fontStyle: 'italic' }}>No operator assigned</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    style={{
                      flex: 1, padding: '12px', background: '#0f3d3e', border: 'none',
                      borderRadius: '8px', color: 'white', fontSize: '0.85rem', fontWeight: '600',
                      cursor: 'pointer', transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                    onClick={() => onBranchClick && onBranchClick(branch.branch_id)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#1a5555'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#0f3d3e'; }}
                  >
                    <i className="pi pi-eye" style={{ fontSize: '0.9rem' }}></i>
                    View Details
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      menuRefs.current[branch.branch_id]?.toggle(e);
                    }}
                    style={{
                      padding: '12px', background: '#f5f5f5', border: 'none', borderRadius: '8px',
                      color: '#0f3d3e', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#e0e0e0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
                  >
                    <i className="pi pi-ellipsis-v" style={{ fontSize: '0.9rem' }}></i>
                  </button>
                  <Menu
                    model={getMenuItems(branch)}
                    popup
                    ref={(el) => { menuRefs.current[branch.branch_id] = el; }}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: '12px', marginTop: '32px', padding: '20px',
          background: 'white', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            style={{
              padding: '10px 16px',
              background: page === 1 ? '#f5f5f5' : '#0f3d3e',
              border: 'none', borderRadius: '10px',
              color: page === 1 ? '#999' : 'white',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '600',
            }}
          >
            <i className="pi pi-chevron-left"></i>
          </button>
          <span style={{ fontSize: '0.9rem', color: '#0f3d3e', fontWeight: '600', padding: '0 12px' }}>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            disabled={page === pagination.pages}
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            style={{
              padding: '10px 16px',
              background: page === pagination.pages ? '#f5f5f5' : '#0f3d3e',
              border: 'none', borderRadius: '10px',
              color: page === pagination.pages ? '#999' : 'white',
              cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
              fontWeight: '600',
            }}
          >
            <i className="pi pi-chevron-right"></i>
          </button>
        </div>
      )}

      {/* ── Modals ── */}
      <AddBranchModal
        visible={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={handleAddBranchSuccess}
        toastRef={toast}
      />
      <EditBranchModal
        visible={showEditModal}
        branch={selectedBranch}
        onHide={() => { setShowEditModal(false); setSelectedBranch(null); }}
        onSuccess={handleEditBranchSuccess}
        toastRef={toast}
      />
    </div>
  );
};

export default Branches;